import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchMetaAdsConnectionStatus,
  isMetaAdsApiConfigured,
} from "./useMetaAdsApi";

/** TanStack Query cache key for Meta connection (stored token in Supabase via meta-ads service). */
export const META_ADS_CONNECTION_STATUS_QUERY_KEY = [
  "meta-ads-connection-status",
];

/**
 * Bumped in localStorage when OAuth (or launch) updates Meta credentials so other tabs refetch.
 * Same-origin tabs receive `storage` events; the writing tab does not (handled via invalidate in-flow).
 */
export const META_ADS_CONNECTION_SYNC_STORAGE_KEY =
  "finitless-meta-ads-connection-sync";

export function broadcastMetaAdsConnectionChanged() {
  try {
    localStorage.setItem(
      META_ADS_CONNECTION_SYNC_STORAGE_KEY,
      String(Date.now()),
    );
  } catch {
    // Private mode / quota
  }
}

/**
 * Connection state from the meta-ads DB mirror (global ``meta_credentials`` row).
 * Refetches on mount, window focus, and when another tab bumps ``META_ADS_CONNECTION_SYNC_STORAGE_KEY``.
 */
export function useMetaAdsConnectionStatus() {
  const queryClient = useQueryClient();
  const apiOk = isMetaAdsApiConfigured();

  useEffect(() => {
    const onStorage = (e) => {
      if (e.storageArea !== localStorage) {
        return;
      }
      if (e.key !== META_ADS_CONNECTION_SYNC_STORAGE_KEY) {
        return;
      }
      queryClient.invalidateQueries({
        queryKey: META_ADS_CONNECTION_STATUS_QUERY_KEY,
      });
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [queryClient]);

  const q = useQuery({
    queryKey: META_ADS_CONNECTION_STATUS_QUERY_KEY,
    queryFn: fetchMetaAdsConnectionStatus,
    enabled: Boolean(apiOk),
    staleTime: 0,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
  });

  const isLoading = Boolean(apiOk && q.isPending);
  const isConnected = Boolean(apiOk && q.data?.connected);

  return {
    isConnected,
    adAccountId: q.data?.adAccountId ?? null,
    businessName: null,
    isLoading,
    isFetching: q.isFetching,
    isError: q.isError,
    error: q.error,
    refetch: q.refetch,
  };
}
